import {
  Box,
  Card,
  Stack,
  Button,
  useTheme,
  CardHeader,
  Typography,
  IconButton,
  CardContent,
  FormControl,
  Grid2 as Grid,
  FormHelperText,
  alpha,
} from '@mui/material';
import type { FormikValues } from 'formik';
import { Formik } from 'formik';
import { varAlpha } from 'minimal-shared/utils';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { UploadIllustration } from 'src/assets/illustrations';
import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';
import { Upload } from 'src/components/upload';
import FileService from 'src/lib/File.service';
import { useTranslate } from 'src/locales';
import { useUpdateClientMutation } from 'src/store/slices/clientsAPISlice';
import { slugify } from 'src/utils/helpers';
import ColorPickerPopover from 'src/components/color-picker/ColorPicker';
import { toast } from 'src/components/snackbar';
import { fData } from 'src/utils/format-number';

type Props = {
  isClientOperator: boolean;
  onToggleEditMode: (setValue?: boolean | null) => void;
  isEditModeEnabled: boolean;
  logoUri: string | null;
  bannerUri: string | null;
  bannerForPdfUri: string | null;
  clientName: string;
  clientId: string;
  brandColor: string;
  formId: string;
  onManuallyUpdateAsset: Dispatch<
    SetStateAction<{
      logo: string;
      banner: string;
    }>
  >;
};

type AssetsType = {
  logo_uri: File | string | null;
  banner_uri: File | string | null;
  banner_for_pdf_uri: File | string | null;
  logoFileSizeError: string | null;
  bannerFileSizeError: string | null;
  bannerForPdfSizeError: string | null;
  brandColor: string;
};

export function Assets({
  isClientOperator,
  onToggleEditMode,
  isEditModeEnabled,
  logoUri,
  bannerUri,
  bannerForPdfUri,
  clientName,
  clientId,
  formId,
  brandColor,
  onManuallyUpdateAsset,
}: Props) {
  const MAX_LOGO_SIZE_KB = (Number(window.RUNTIME_CONFIG.MAX_LOGO_SIZE_KB) || 200) * 1024;
  const MAX_BANNER_SIZE_KB = (Number(window.RUNTIME_CONFIG.MAX_BANNER_SIZE_KB) || 500) * 1024;

  const theme = useTheme();
  const { t } = useTranslate();

  const [updateClientMedia, { isLoading: updateClientMediaLoading }] = useUpdateClientMutation();

  const FILE_SERVICE = window.RUNTIME_CONFIG.FILE_SERVICE;

  const [assets, setAssets] = useState<AssetsType>({
    logo_uri: null,
    banner_uri: null,
    banner_for_pdf_uri: null,
    logoFileSizeError: null,
    bannerFileSizeError: null,
    bannerForPdfSizeError: null,
    brandColor: brandColor || '002A58',
  });

  useEffect(() => {
    setAssets({
      logo_uri: logoUri,
      banner_uri: bannerUri,
      banner_for_pdf_uri: bannerForPdfUri,
      logoFileSizeError: null,
      bannerFileSizeError: null,
      bannerForPdfSizeError: null,
      brandColor,
    });
  }, [logoUri, bannerUri, bannerForPdfUri, clientName, clientId, brandColor]);

  // Upload assets
  const uploadAssets = async (
    logoFile: File | string | null,
    bannerFile: File | string | null,
    bannerForPdfFile: File | string | null,
    client: { name: string; id: string; brandColor?: string }
  ) => {
    const clientSlug = slugify(client.name);

    if (logoFile instanceof File) {
      if (logoFile.size !== 0) {
        await FileService.uploadClientLogo(logoFile, clientSlug, clientId).then(() => {
          updateClientMedia({
            id: client.id,
            body: {
              logo_uri: `${FILE_SERVICE}/${clientSlug}/${clientSlug}-logo.png`,
              brandColor: client.brandColor || '#002A58',
            },
          }).unwrap();
        });
      }
    }

    if (bannerFile instanceof File) {
      if (bannerFile.size !== 0) {
        await FileService.uploadClientBanner(bannerFile, clientSlug, clientId).then(() => {
          updateClientMedia({
            id: client.id,
            body: {
              banner_uri: `${FILE_SERVICE}/${clientSlug}/${clientSlug}-banner.png`,
              brandColor: client.brandColor || '#002A58',
            },
          }).unwrap();
        });
      }
    }

    if (bannerForPdfFile instanceof File) {
      if (bannerForPdfFile.size !== 0) {
        await FileService.uploadClientBannerForPdf(bannerForPdfFile, clientSlug, clientId).then(
          () => {
            updateClientMedia({
              id: client.id,
              body: {
                banner_for_pdf_uri: `${FILE_SERVICE}/${clientSlug}/${clientSlug}-banner-for-pdf.png`,
                brandColor: client.brandColor || '#002A58',
              },
            }).unwrap();
          }
        );
      }
    }

    if (!logoFile && logoUri) {
      await FileService.delete(clientSlug, 'logo').then(() => {
        updateClientMedia({
          id: client.id,
          body: {
            logo_uri: null,
            brandColor: client.brandColor || '#002A58',
          },
        }).unwrap();
      });
    }

    if (!bannerFile && bannerUri) {
      await FileService.delete(clientSlug, 'banner').then(() => {
        updateClientMedia({
          id: client.id,
          body: {
            banner_uri: null,
            brandColor: client.brandColor || '#002A58',
          },
        }).unwrap();
      });
    }

    if (!bannerForPdfFile && bannerForPdfUri) {
      await FileService.delete(clientSlug, 'banner-for-pdf').then(() => {
        updateClientMedia({
          id: client.id,
          body: {
            banner_for_pdf_uri: null,
            brandColor: client.brandColor || '#002A58',
          },
        }).unwrap();
      });
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (fileData: File[]) => {
    if (fileData[0].size > MAX_LOGO_SIZE_KB) {
      setAssets((prev) => ({
        ...prev,
        logo_uri: fileData[0],
        logoFileSizeError: t('pages.clientsPage.modal.fileSizeError', {
          size: fData(MAX_LOGO_SIZE_KB),
        }),
      }));
    } else {
      setAssets((prev) => ({
        ...prev,
        logo_uri: fileData[0],
        logoFileSizeError: null,
      }));
    }
  };

  // Handle banner upload
  const handleBannerUpload = async (fileData: File[]) => {
    if (fileData[0].size > MAX_BANNER_SIZE_KB) {
      setAssets((prev) => ({
        ...prev,
        banner_uri: fileData[0],
        bannerFileSizeError: t('pages.clientsPage.modal.fileSizeError', {
          size: fData(MAX_BANNER_SIZE_KB),
        }),
      }));
    } else {
      setAssets((prev) => ({
        ...prev,
        banner_uri: fileData[0],
        bannerFileSizeError: null,
      }));
    }
  };

  // Handle baner for pdf upload
  const handleBannerForPdfUpload = async (fileData: File[]) => {
    if (fileData[0].size > MAX_BANNER_SIZE_KB) {
      setAssets((prev) => ({
        ...prev,
        banner_for_pdf_uri: fileData[0],
        bannerForPdfSizeError: t('pages.clientsPage.modal.fileSizeError', {
          size: fData(MAX_BANNER_SIZE_KB),
        }),
      }));
    } else {
      setAssets((prev) => ({
        ...prev,
        banner_for_pdf_uri: fileData[0],
        bannerForPdfSizeError: null,
      }));
    }
  };

  // Handle form submit
  const handleFormSubmit = async (values: FormikValues) => {
    if (assets.logoFileSizeError || assets.bannerFileSizeError || assets.bannerForPdfSizeError) {
      return;
    }

    await updateClientMedia({
      id: clientId,
      body: {
        brandColor: values.brandColor,
      },
    })
      .unwrap()
      .then(() => {
        onManuallyUpdateAsset({
          logo:
            values.logo_uri && typeof values.logo_uri !== 'string'
              ? URL.createObjectURL(values.logo_uri)
              : '',
          banner:
            values.banner_uri && typeof values.banner_uri !== 'string'
              ? URL.createObjectURL(values.banner_uri)
              : '',
        });

        uploadAssets(values.logo_uri, values.banner_uri, values.banner_for_pdf_uri, {
          name: clientName,
          id: clientId,
          brandColor: values.brandColor,
        });
      });

    toast.success(t('toastMessages.clientUpdatedSuccesfull'), { position: 'bottom-right' });
  };

  return (
    isClientOperator && (
      <Grid size={7}>
        <Formik
          initialValues={{
            logo_uri: assets.logo_uri,
            banner_uri: assets.banner_uri,
            banner_for_pdf_uri: assets.banner_for_pdf_uri,
            brandColor: assets.brandColor,
          }}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit} id={formId}>
              <Card>
                <CardHeader
                  title={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">{t('common.media')}</Typography>
                      {isClientOperator && (
                        <IconButton
                          color="default"
                          sx={{
                            borderRadius: '8px',
                          }}
                          onClick={() => onToggleEditMode()}
                          data-test-id="edit-row-link"
                        >
                          <Iconify icon="lucide:pencil" color={theme.palette.text.secondary} />
                        </IconButton>
                      )}
                    </Stack>
                  }
                  sx={{
                    padding: theme.spacing(3),
                    paddingRight: theme.spacing(2),
                  }}
                />

                <CardContent sx={{ pt: 0 }}>
                  {/* Show alert if edit mode is enabled */}
                  {isEditModeEnabled && (
                    <Card
                      sx={{
                        mb: theme.spacing(3),
                        display: 'flex',
                        alignItems: 'center',
                        p: '4px',
                        pr: 0,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: alpha(theme.palette.warning.light, 0.18),
                          width: '48px',
                          height: '48px',
                          borderRadius: theme.spacing(1.5),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: theme.spacing(1),
                        }}
                      >
                        <Iconify
                          icon="solar:danger-triangle-bold"
                          color={theme.palette.warning.main}
                        />
                      </Box>
                      <Typography variant="subtitle2">{t('common.unsavedChanges')}</Typography>
                    </Card>
                  )}

                  <Box
                    sx={{
                      rowGap: 2,
                      columnGap: 2,
                      display: 'grid',
                    }}
                    gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  >
                    {/* Logo */}
                    <Box
                      sx={{
                        gridColumn: {
                          md: 'span 2',
                          xs: 'span 4',
                        },
                        pt: 0,
                      }}
                    >
                      <Label
                        sx={{
                          backgroundColor: 'white',
                          justifyContent: 'flex-start',
                          mb: theme.spacing(1.5),
                        }}
                      >
                        {isClientOperator && isEditModeEnabled
                          ? t('forms.labels.uploadLogo')
                          : t('forms.labels.logo')}
                      </Label>

                      <Box
                        sx={{
                          p: logoUri ? 0 : 3,
                          outline: 'none',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          bgcolor: varAlpha(theme.palette.grey['500Channel'], 0.08),
                          border: `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
                          transition: theme.transitions.create(['opacity', 'padding']),
                          height: '350px',
                          cursor: 'pointer',
                          '@media (max-width: 1024px)': {
                            height: '200px',
                          },
                        }}
                        onClick={() => onToggleEditMode(true)}
                      >
                        {logoUri && !isEditModeEnabled ? (
                          <Box
                            component="img"
                            src={logoUri}
                            alt={`Logo for ${clientName}`}
                            sx={{
                              width: '100%',
                              height: 'auto',
                              objectFit: 'cover',
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0,
                            }}
                          >
                            {isClientOperator && isEditModeEnabled ? (
                              <Upload
                                value={assets.logo_uri}
                                onDrop={handleLogoUpload}
                                title={
                                  isClientOperator && isEditModeEnabled
                                    ? t('forms.labels.uploadLogo')
                                    : t('forms.labels.logo')
                                }
                                onDelete={() => {
                                  setAssets((prev) => ({
                                    ...prev,
                                    logo_uri: null,
                                    logoFileSizeError: null,
                                  }));
                                }}
                                sx={{
                                  height: '100%',
                                  '& > div:first-of-type': {
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  },
                                }}
                                disabled={updateClientMediaLoading}
                              />
                            ) : (
                              <>
                                <UploadIllustration hideBackground sx={{ width: 200 }} />
                                <Typography variant="h5">
                                  {t('forms.labels.noUploadedLogo')}
                                </Typography>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>

                      {assets.logoFileSizeError && (
                        <FormHelperText error>{assets.logoFileSizeError}</FormHelperText>
                      )}
                    </Box>

                    {/* Banner */}
                    <Box
                      sx={{
                        gridColumn: {
                          md: 'span 2',
                          xs: 'span 4',
                        },
                        pt: 0,
                      }}
                    >
                      <Label
                        sx={{
                          backgroundColor: 'white',
                          justifyContent: 'flex-start',
                          mb: theme.spacing(1.5),
                        }}
                      >
                        {isClientOperator && isEditModeEnabled
                          ? t('forms.labels.uploadBanner')
                          : t('forms.labels.banner')}{' '}
                      </Label>

                      <Box
                        sx={{
                          outline: 'none',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          bgcolor: varAlpha(theme.palette.grey['500Channel'], 0.08),
                          border: `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
                          transition: theme.transitions.create(['opacity', 'padding']),
                          height: '350px',
                          cursor: 'pointer',
                          '@media (max-width: 1024px)': {
                            height: '200px',
                          },
                        }}
                        onClick={() => onToggleEditMode(true)}
                      >
                        {bannerUri && !isEditModeEnabled ? (
                          <Box
                            component="img"
                            src={bannerUri}
                            alt={`Banner for ${clientName}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0,
                            }}
                          >
                            {isClientOperator && isEditModeEnabled ? (
                              <Upload
                                value={assets.banner_uri}
                                onDrop={handleBannerUpload}
                                title={
                                  isClientOperator && isEditModeEnabled
                                    ? t('forms.labels.uploadBanner')
                                    : t('forms.labels.banner')
                                }
                                onDelete={() => {
                                  setAssets((prev) => ({
                                    ...prev,
                                    banner_uri: null,
                                    bannerFileSizeError: null,
                                  }));
                                }}
                                sx={{
                                  height: '100%',
                                  '& > div:first-of-type': {
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  },
                                }}
                                disabled={updateClientMediaLoading}
                              />
                            ) : (
                              <>
                                <UploadIllustration hideBackground sx={{ width: 200 }} />
                                <Typography variant="h5">
                                  {t('forms.labels.noUploadedBanner')}
                                </Typography>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>

                      {assets.bannerFileSizeError && (
                        <FormHelperText error>{assets.bannerFileSizeError}</FormHelperText>
                      )}
                    </Box>

                    {/* Banner for PDF*/}
                    <Stack
                      sx={{ gridColumn: 'span 4', pt: 0, cursor: 'pointer' }}
                      spacing={1.5}
                      onClick={() => onToggleEditMode(true)}
                    >
                      <Label sx={{ backgroundColor: 'white', justifyContent: 'flex-start' }}>
                        {isClientOperator && isEditModeEnabled
                          ? t('forms.labels.uploadBannerForPdf')
                          : t('forms.labels.bannerForPdf')}{' '}
                      </Label>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        alignItems="center"
                        display="flex"
                        gap={0.5}
                      >
                        <Iconify icon="mdi:error" />
                        {t('common.bannerWillBeCropped', { width: 400, height: 400 })}
                      </Typography>

                      <Box
                        sx={{
                          outline: 'none',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          bgcolor: varAlpha(theme.palette.grey['500Channel'], 0.08),
                          border: `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
                          transition: theme.transitions.create(['opacity', 'padding']),
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                          paddingTop: '40%',
                          height: {
                            xs: '200px',
                          },
                        }}
                      >
                        {bannerForPdfUri && !isEditModeEnabled ? (
                          <Box
                            component="img"
                            src={bannerForPdfUri}
                            alt={`Banner PDF for ${clientName}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              position: 'absolute',
                              top: '0',
                              left: '0',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '0',
                              left: '0',
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0,
                            }}
                          >
                            {isClientOperator && isEditModeEnabled ? (
                              <Upload
                                value={assets.banner_for_pdf_uri}
                                onDrop={handleBannerForPdfUpload}
                                title={
                                  isClientOperator && isEditModeEnabled
                                    ? t('forms.labels.uploadBannerForPdf')
                                    : t('forms.labels.bannerForPdf')
                                }
                                onDelete={() => {
                                  setAssets((prev) => ({
                                    ...prev,
                                    banner_for_pdf_uri: null,
                                    bannerForPdfSizeError: null,
                                  }));
                                }}
                                sx={{
                                  height: '100%',
                                  '& > div:first-of-type': {
                                    height: '100%',
                                    padding: '20% 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  },
                                }}
                                disabled={updateClientMediaLoading}
                              />
                            ) : (
                              <>
                                <UploadIllustration hideBackground sx={{ width: 200 }} />
                                <Typography variant="h5">
                                  {t('forms.labels.noUploadedBannerBannerForPdf')}
                                </Typography>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>

                      {assets.bannerForPdfSizeError && (
                        <FormHelperText error>{assets.bannerForPdfSizeError}</FormHelperText>
                      )}
                    </Stack>

                    {/* Color picker */}
                    <FormControl
                      sx={{ gridColumn: 'span 4', mt: theme.spacing(2) }}
                      onClick={() => onToggleEditMode(true)}
                    >
                      <ColorPickerPopover
                        name="brandColor"
                        formik={formik}
                        label={t('forms.labels.brandColor')}
                        placeholder={t('forms.placeholders.brandColor')}
                        disabled={!isEditModeEnabled}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        alignItems="center"
                        display="flex"
                        gap={0.5}
                      >
                        <Iconify icon="mdi:error" />
                        {t('common.selectingBrandColorHelperText')}
                      </Typography>
                    </FormControl>
                  </Box>

                  {isEditModeEnabled && (
                    <Box sx={{ mt: theme.spacing(3), display: 'flex', justifyContent: 'flex-end' }}>
                      <Stack direction="row" spacing={theme.spacing(1.5)}>
                        <Button
                          data-test-id="close-btn"
                          variant="outlined"
                          onClick={() => onToggleEditMode(false)}
                          disabled={updateClientMediaLoading}
                          loading={updateClientMediaLoading}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          disabled={updateClientMediaLoading}
                          loading={updateClientMediaLoading}
                          type="submit"
                          form={formId}
                          color="primary"
                          variant="contained"
                        >
                          {t('buttons.saveChanges')}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </form>
          )}
        </Formik>
      </Grid>
    )
  );
}
